/*eslint-disable*/
import React, { useEffect, useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment,
  Box,
  Avatar,
  Divider,
  IconButton,
  DialogContentText,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useSelector, useDispatch } from "react-redux";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { FirebaseContext } from "common";

/**
 * DoctorSearchModal
 *
 * - Normalizes practice shapes into a canonical single-practice-per-doctor model
 * - Supports optional location-based filtering (radius)
 * - Shows a single chosen practice per doctor (no multiple-practice UI)
 * - Lets the user pick a 30-minute slot; returns ISO datetimes to parent
 *
 * Props:
 *  - open
 *  - onClose
 *  - doctors (array)
 *  - doctortypes (array)
 *  - specialitytypes (array)
 *  - onAssign (optional) -> called when doctor is selected
 *  - onCreateBooking (optional) -> called when booking is to be created:
 *        onCreateBooking(docWithPractice, startIso, endIso)
 *  - onAssignDate (optional) -> called with (startIso, endIso) when a slot is chosen
 *  - defaultMaxDistanceKm
 *  - initialSlotDate (optional) - prefer ISO string; used to seed slot generation day
 */
export default function DoctorSearchModal({
  open,
  onClose,
  doctors = [],
  doctortypes = [],
  specialitytypes = [],
  onAssign,
  onCreateBooking,
  onAssignDate, // NEW: callback to notify parent of chosen ISO start/end
  defaultMaxDistanceKm = 50,
  initialSlotDate = null,
}) {
  const dispatch = useDispatch();
  const { api } = useContext(FirebaseContext);

  // expose api methods if you need them (estimate, bookings etc.)
  const {
    getEstimate,
    clearEstimate,
    addBooking,
    clearBooking,
    MinutesPassed,
    GetDateString,
    fetchUser,
    fetchUsers,
    updateBooking,
    cancelBooking,
    fetchBookings,
  } = api;

  // redux state used by this modal
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const practicetypes = useSelector((state) => state.practicetypes);
  const estimatedata = useSelector((state) => state.estimatedata);

  // ----------------------
  // Local UI state
  // ----------------------
  const [searchTerm, setSearchTerm] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [enrichedDoctors, setEnrichedDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  // *NO* multiple-practice selection state — we keep a single _practice per doctor
  const [useLocation, setUseLocation] = useState(true);
  const [maxDistanceKm, setMaxDistanceKm] = useState(defaultMaxDistanceKm);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [requestingLocation, setRequestingLocation] = useState(false);

  // slots
  const [availableSlots, setAvailableSlots] = useState([]);
  const [chosenSlot, setChosenSlot] = useState(null);

  // estimate dialog state
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  const [estimateRequested, setEstimateRequested] = useState(false);

  // ----------------------
  // Small helpers
  // ----------------------
  const pad = (n) => String(n).padStart(2, "0");

  // format a Date -> "YYYY-MM-DDTHH:mm" (good for display or legacy usage)
  const formatForDatetimeLocal = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // parse a value to Date or null
  const parseDateOrNull = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  // haversine distance in km
  const haversineKm = (lat1, lon1, lat2, lon2) => {
    if ([lat1, lon1, lat2, lon2].some((v) => v == null || Number.isNaN(Number(v)))) return null;
    const toRad = (d) => (d * Math.PI) / 180.0;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // ----------------------
  // Normalize practice objects
  // - Handles many shapes:
  //   - practice.coords.lat/lng
  //   - address.coords.lat/lng
  //   - coords.lat/lng
  //   - address.lat/lng
  //   - location.lat/lng
  //   - top-level lat/lng
  // - Returns: { practiceName, practiceNumber, address (string), lat, lng, original, addressObj, locationObj }
  // ----------------------
  const normalizePractice = (p) => {
    if (!p) return null;

    // safe getter (nested paths)
    const get = (obj, path) => {
      try {
        return path.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
      } catch {
        return undefined;
      }
    };

    // coordinates candidates (check in useful order)
    const coordFromAddressCoords =
      get(p, "address.coords.lat") != null && get(p, "address.coords.lng") != null
        ? { lat: Number(get(p, "address.coords.lat")), lng: Number(get(p, "address.coords.lng")) }
        : null;

    const coordFromPracticeCoords =
      get(p, "practice.coords.lat") != null && get(p, "practice.coords.lng") != null
        ? { lat: Number(get(p, "practice.coords.lat")), lng: Number(get(p, "practice.coords.lng")) }
        : null;

    const coordFromCoords =
      get(p, "coords.lat") != null && get(p, "coords.lng") != null
        ? { lat: Number(get(p, "coords.lat")), lng: Number(get(p, "coords.lng")) }
        : null;

    const coordFromAddressObj =
      get(p, "address.lat") != null && get(p, "address.lng") != null
        ? { lat: Number(get(p, "address.lat")), lng: Number(get(p, "address.lng")) }
        : null;

    const coordFromLocation =
      get(p, "location.lat") != null && get(p, "location.lng") != null
        ? { lat: Number(get(p, "location.lat")), lng: Number(get(p, "location.lng")) }
        : null;

    const coordFromTop = p.lat != null && p.lng != null ? { lat: Number(p.lat), lng: Number(p.lng) } : null;

    const coords = coordFromAddressCoords || coordFromPracticeCoords || coordFromCoords || coordFromAddressObj || coordFromLocation || coordFromTop || null;

    // readable address selection
    const addrString = (() => {
      const a = p.address || (p.practice && typeof p.practice === "object" ? p.practice.address : undefined);
      if (typeof a === "string" && a.trim()) return a;
      if (a && typeof a === "object") {
        if (a.formattedAddress) return a.formattedAddress;
        if (a.formatted_address) return a.formatted_address;
        if (a.formatted) return a.formatted;
        if (a.displayName) return a.displayName;
        if (a.name) return a.name;
      }
      if (p.practice && typeof p.practice === "object") {
        if (p.practice.formattedAddress) return p.practice.formattedAddress;
        if (p.practice.formatted_address) return p.practice.formatted_address;
        if (p.practice.name) return p.practice.name;
      }
      if (p.practiceAddress && typeof p.practiceAddress === "string" && p.practiceAddress.trim()) return p.practiceAddress;
      if (p.addressLine && typeof p.addressLine === "string" && p.addressLine.trim()) return p.addressLine;
      if (p.extra_info && typeof p.extra_info === "string" && p.extra_info.trim()) return p.extra_info;
      if (coords) return `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
      return "";
    })();

    return {
      ...p,
      practiceName: p.practiceName || (p.practice && p.practice.name) || p.practice || p.name || "",
      practiceNumber: p.practiceNumber || p.practiceNo || (p.practice && p.practice.practiceNumber) || p.id || "",
      address: addrString,
      addressObj: p.address || (p.practice && p.practice.address) || null,
      locationObj: p.location || null,
      lat: coords ? coords.lat : null,
      lng: coords ? coords.lng : null,
      original: p,
    };
  };

  // ----------------------
  // Build practices list for a doctor
  // - returns array (but later we collapse to single chosen practice)
  // ----------------------
  const practicesForDoctor = (doc, practicesIndex = []) => {
    const out = [];

    // 1) explicit array on doctor object
    if (Array.isArray(doc.practices) && doc.practices.length) {
      for (const p of doc.practices) {
        const np = normalizePractice(p);
        if (np) out.push(np);
      }
    }

    // 2) doctor-level practice fields or coords
    const hasDirectPracticeFields =
      doc.practice || doc.practiceName || doc.practiceAddress || doc.address || doc.location || (doc.lat != null && doc.lng != null) || doc.coords || (doc.practice && doc.practice.coords);
    if (hasDirectPracticeFields) {
      // forward coords if present so normalizePractice can pick them up
      const p = {
        practiceName: doc.practice && typeof doc.practice === "object" ? doc.practice.name : (doc.practice || doc.practiceName || ""),
        practiceNumber: doc.practiceNumber || (doc.practice && doc.practice.practiceNumber) || "",
        address: doc.practice && typeof doc.practice === "object" ? (doc.practice.address || "") : (doc.practiceAddress || doc.address || ""),
        addressObj: typeof doc.address === "object" ? doc.address : (doc.practice && typeof doc.practice === "object" ? doc.practice.address : null),
        locationObj: doc.location || (doc.practice && typeof doc.practice === "object" ? doc.practice.location : null),
        coords: doc.coords || (doc.practice && doc.practice.coords) || undefined,
        lat: doc.lat || (doc.location && doc.location.lat) || (doc.address && doc.address.lat) || null,
        lng: doc.lng || (doc.location && doc.location.lng) || (doc.address && doc.address.lng) || null,
        extra_info: doc.extra_info || "",
      };
      out.push(normalizePractice(p));
    }

    // 3) try match against a global practice index by name/number
    if (Array.isArray(practicesIndex) && practicesIndex.length) {
      for (const p of practicesIndex) {
        const np = normalizePractice(p);
        const matchesNumber = doc.practiceNumber && np.practiceNumber && String(np.practiceNumber) === String(doc.practiceNumber);
        const matchesName =
          doc.practice &&
          np.practiceName &&
          np.practiceName.toLowerCase().trim() === (doc.practice || "").toLowerCase().trim();
        if (matchesNumber || matchesName) {
          if (!out.some((r) => r.practiceNumber && r.practiceNumber === np.practiceNumber)) out.push(np);
        }
      }
    }

    // dedupe by address+coords+practiceNumber
    const seen = new Set();
    const dedup = [];
    for (const p of out) {
      const key = `${p.address || ""}_${p.lat ?? ""}_${p.lng ?? ""}_${p.practiceNumber || ""}`;
      if (!seen.has(key)) {
        seen.add(key);
        dedup.push(p);
      }
    }
    return dedup;
  };

  // ----------------------
  // Request user location (browser geolocation)
  // ----------------------
  const requestUserLocation = () => {
    setLocationError(null);
    if (!navigator || !navigator.geolocation) {
      setLocationError("Geolocation not supported by this browser.");
      return;
    }
    setRequestingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setRequestingLocation(false);
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => {
        setRequestingLocation(false);
        setLocationError(err.message || "Unable to retrieve location.");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // ----------------------
  // Enrich doctors with a single chosen practice + coords + distance
  // - chooses nearest practice with coords when possible
  // - otherwise picks first practice
  // - sets _practices (array length 0 or 1), _practice (single), _coords, _distanceKm
  // ----------------------
  useEffect(() => {
    const practicesIndex = Array.isArray(practicetypes?.practices) ? practicetypes.practices.slice() : [];
    const list = Array.isArray(doctors) ? doctors.slice() : [];

    const enriched = list.map((doc) => {
      const pracList = practicesForDoctor(doc, practicesIndex);
      let chosenPractice = null;
      let finalCoords = null;

      // prefer nearest practice with coords (if we have userLocation)
      if (pracList && pracList.length) {
        if (userLocation) {
          let best = null;
          for (const p of pracList) {
            if (p.lat != null && p.lng != null) {
              const dist = haversineKm(userLocation.lat, userLocation.lng, p.lat, p.lng);
              if (!best || dist < best.dist) best = { practice: p, dist };
            }
          }
          if (best) {
            chosenPractice = best.practice;
            finalCoords = { lat: best.practice.lat, lng: best.practice.lng };
          }
        }

        // fallback: first practice with coords
        if (!chosenPractice) {
          const withCoords = pracList.find((p) => p.lat != null && p.lng != null);
          if (withCoords) {
            chosenPractice = withCoords;
            finalCoords = { lat: withCoords.lat, lng: withCoords.lng };
          }
        }

        // last resort: first practice even without coords
        if (!chosenPractice) {
          chosenPractice = pracList[0];
          finalCoords = chosenPractice.lat != null && chosenPractice.lng != null ? { lat: chosenPractice.lat, lng: chosenPractice.lng } : null;
        }
      } else {
        // no practices found: try doc-level coords shapes
        if (doc.lat != null && doc.lng != null) finalCoords = { lat: Number(doc.lat), lng: Number(doc.lng) };
        else if (doc.coords && doc.coords.lat != null && doc.coords.lng != null) finalCoords = { lat: Number(doc.coords.lat), lng: Number(doc.coords.lng) };
        else if (doc.practice && doc.practice.coords && doc.practice.coords.lat != null && doc.practice.coords.lng != null)
          finalCoords = { lat: Number(doc.practice.coords.lat), lng: Number(doc.practice.coords.lng) };
      }

      const distanceKm = userLocation && finalCoords ? haversineKm(userLocation.lat, userLocation.lng, finalCoords.lat, finalCoords.lng) : null;

      // collapse to single-practice array for compatibility (length 0 or 1)
      const finalPracticeList = chosenPractice ? [chosenPractice] : (pracList.length ? [pracList[0]] : []);

      return {
        ...doc,
        _practices: finalPracticeList,
        _practice: chosenPractice || (finalPracticeList[0] || null),
        _coords: finalCoords,
        _hasPracticeAddress: Boolean((chosenPractice && chosenPractice.address) || doc.practiceAddress || doc.address),
        _distanceKm: distanceKm,
      };
    });

    setEnrichedDoctors(enriched);
  }, [doctors, practicetypes, userLocation]);

  // ----------------------
  // Filter and sort logic for the visible doctor list
  // ----------------------
  useEffect(() => {
    let docs = (enrichedDoctors || []).slice();

    // only doctors
    docs = docs.filter((d) => d && (d.userType === "doctor" || d.role === "doctor" || d.isDoctor));

    // speciality filter
    if (filterValue && filterValue !== "") {
      docs = docs.filter((d) => {
        const speciality = (d.specialityType || d.practice || d.doctorSpeciality || "") + "";
        return speciality.toLowerCase().includes(String(filterValue).toLowerCase());
      });
    }

    // name/email/mobile search
    if (searchTerm && searchTerm.trim() !== "") {
      const t = searchTerm.toLowerCase();
      docs = docs.filter(
        (d) =>
          (d.firstName && d.firstName.toLowerCase().includes(t)) ||
          (d.lastName && d.lastName.toLowerCase().includes(t)) ||
          (d.email && d.email.toLowerCase().includes(t)) ||
          (d.mobile && d.mobile.toLowerCase().includes(t))
      );
    }

    // if using location: require coords, filter by radius, sort by distance
    if (useLocation) {
      docs = docs.filter((d) => d._practices && d._practices.length && d._coords);
      docs = docs.filter((d) => d._distanceKm != null && d._distanceKm <= Number(maxDistanceKm));
      docs.sort((a, b) => (a._distanceKm || Infinity) - (b._distanceKm || Infinity));
    } else {
      // alphabetical if not using location
      docs.sort((a, b) => {
        const aName = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
        const bName = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
        return aName.localeCompare(bName);
      });
    }

    setFilteredDoctors(docs);
  }, [enrichedDoctors, filterValue, searchTerm, useLocation, maxDistanceKm]);

  // if modal opens and useLocation is enabled, request location
  useEffect(() => {
    if (open && useLocation) requestUserLocation();
    if (!open) {
      // reset view state when modal closes
      setSearchTerm("");
      setFilterValue("");
      setSelectedDoctor(null);
      setAvailableSlots([]);
      setChosenSlot(null);
      setUserLocation(null);
      setLocationError(null);
    }
  }, [open, useLocation]);

  // ----------------------
  // Slot helpers
  // ----------------------
  const getBookingRange = (b) => {
    if (!b) return null;
    const parseLocal = (v) => parseDateOrNull(v);
    const startKeys = ["startDate", "startdate", "eventDate", "eventdate"];
    const endKeys = ["endDate", "enddate", "eventEndDate", "eventEnd"];
    let start = null,
      end = null;
    for (const k of startKeys) if (b[k]) { start = parseLocal(b[k]); if (start) break; }
    for (const k of endKeys) if (b[k]) { end = parseLocal(b[k]); if (end) break; }
    if (!start && b.eventDate) start = parseLocal(b.eventDate);
    if (start && !end) {
      const dur = (b.estimate?.estimateTime && Number(b.estimate?.estimateTime)) || 30;
      end = new Date(start.getTime() + dur * 60000);
    }
    if (!start) return null;
    return { start, end };
  };

  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const overlaps = (aS, aE, bS, bE) => aS < bE && bS < aE;

  // read practice hours; fallback to 8..17
  const getPracticeHours = (practice) => {
    if (!practice) return { startHour: 8, endHour: 17 };
    const tryParse = (v, fallback) => {
      if (v == null) return fallback;
      if (typeof v === "number") return v;
      if (typeof v === "string") {
        const m = v.match(/^(\d{1,2})(?::\d{2})?/);
        if (m) return Number(m[1]);
      }
      return fallback;
    };
    return { startHour: tryParse(practice.openingHour, 8), endHour: tryParse(practice.closingHour, 17) };
  };

  // generate 30-minute slots for a date object
  const generateSlots = (dateObj, startHour, endHour, intervalMinutes = 30) => {
    const slots = [];
    const dayStart = new Date(dateObj); dayStart.setHours(startHour, 0, 0, 0);
    const dayEnd = new Date(dateObj); dayEnd.setHours(endHour, 0, 0, 0);
    for (let t = dayStart.getTime(); t + intervalMinutes * 60000 <= dayEnd.getTime(); t += intervalMinutes * 60000) {
      slots.push({ start: new Date(t), end: new Date(t + intervalMinutes * 60000) });
    }
    return slots;
  };

  // When a doctor is selected, compute available slots for the (single) practice on initialSlotDate or today
  useEffect(() => {
    setAvailableSlots([]);
    setChosenSlot(null);
    if (!selectedDoctor) return;

    // single practice used
    const practice = selectedDoctor._practice || (Array.isArray(selectedDoctor._practices) ? selectedDoctor._practices[0] : null);

    // determine working hours
    const { startHour, endHour } = getPracticeHours(practice);

    // date to show slots for
    const dateToUse = initialSlotDate ? parseDateOrNull(initialSlotDate) : new Date();
    if (!dateToUse) return;

    // gather bookings for the doctor on that day to mark conflicts
    const bookings = Array.isArray(bookinglistdata?.bookings) ? bookinglistdata.bookings : [];
    const doctorBookingsForDay = [];
    for (const b of bookings) {
      const bookingDoctor = b.doctor || b.doctorDetails || null;
      let isSameDoctor = false;
      if (bookingDoctor) {
        const buid = bookingDoctor.uid || bookingDoctor.id || bookingDoctor._id;
        const suid = selectedDoctor.uid || selectedDoctor.id || selectedDoctor._id;
        if (suid && buid && String(buid) === String(suid)) isSameDoctor = true;
        if (!isSameDoctor && bookingDoctor.practiceNumber && practice && practice.practiceNumber) {
          if (String(bookingDoctor.practiceNumber) === String(practice.practiceNumber)) isSameDoctor = true;
        }
      }
      if (!isSameDoctor) continue;

      const range = getBookingRange(b);
      if (!range) continue;
      if (sameDay(range.start, dateToUse)) doctorBookingsForDay.push(range);
    }

    // create candidate slots and mark conflicts / past slots
    const candidates = generateSlots(dateToUse, startHour, endHour, 30);
    const now = new Date();
    const slotsWithDisabled = candidates.map((slot) => {
      const isPast = sameDay(slot.start, now) && slot.end <= now;
      const conflict = doctorBookingsForDay.some((r) => overlaps(slot.start, slot.end, r.start, r.end));
      return { start: slot.start, end: slot.end, disabled: isPast || conflict, conflictWith: conflict };
    });

    setAvailableSlots(slotsWithDisabled);
  }, [selectedDoctor, bookinglistdata?.bookings, initialSlotDate, practicetypes, userLocation]);

  // ----------------------
  // UI interaction handlers
  // ----------------------

  // called when user clicks "Select" in the doctor list
  const handleSelectDoctor = (doc) => {
    setSelectedDoctor(doc);
    if (onAssign) onAssign(doc); // optional parent notification
  };

  // back to list view
  const handleBackToList = () => {
    setSelectedDoctor(null);
    setAvailableSlots([]);
    setChosenSlot(null);
  };

  // IMPORTANT:
  // When confirming a slot we *notify parent* using ISO timestamps (to preserve time)
  // We also call onCreateBooking for backward compatibility (it receives ISO start/end as well).
  const handleConfirmSlot = () => {
    if (!selectedDoctor || !chosenSlot) return;

    const docWithPractice = {
      ...selectedDoctor,
      practice: selectedDoctor._practice || (selectedDoctor._practices && selectedDoctor._practices[0]) || null,
    };

    // Use ISO strings (UTC) so parent/backend doesn't lose time info
    const startIso = chosenSlot.start instanceof Date ? chosenSlot.start.toISOString() : new Date(chosenSlot.start).toISOString();
    const endIso = chosenSlot.end instanceof Date ? chosenSlot.end.toISOString() : new Date(chosenSlot.end).toISOString();

    // Notify parent of chosen ISO datetimes (parent should accept ISO)
    if (typeof onAssignDate === "function") {
      try { onAssignDate(startIso, endIso); } catch (err) { /* ignore errors from parent callback */ }
    }

    // Call onCreateBooking if provided — pass ISO strings as well
    if (typeof onCreateBooking === "function") {
      try { onCreateBooking(docWithPractice, startIso, endIso); } catch (err) { /* ignore */ }
    }
  };

  // If user wants an estimate then accepts, also notify parent and create booking
  const handleEstimateAndOpenDialog = () => {
    if (!selectedDoctor || !chosenSlot) return;
    const DUMMY_COORDS = { lat: -26.2041, lng: 28.0473 };

    const practice = selectedDoctor._practice || (selectedDoctor._practices && selectedDoctor._practices[0]) || null;
    const serviceType = (practice && (practice.address || (practice.lat != null && practice.lng != null))) ? "Home Consultation" : "Video Conference";
    const bookingDate = formatForDatetimeLocal(chosenSlot.start);

    const docCoords = practice && practice.lat != null && practice.lng != null
      ? { lat: Number(practice.lat), lng: Number(practice.lng) }
      : selectedDoctor._coords
        ? { lat: Number(selectedDoctor._coords.lat), lng: Number(selectedDoctor._coords.lng) }
        : DUMMY_COORDS;

    const pickupCoords = userLocation && userLocation.lat != null && userLocation.lng != null
      ? { lat: Number(userLocation.lat), lng: Number(userLocation.lng) }
      : docCoords || DUMMY_COORDS;

    const bookingData = {
      serviceType,
      pickup: {
        coords: { lat: Number(pickupCoords.lat), lng: Number(pickupCoords.lng) },
        address: userLocation?.address || null,
      },
      drop: {
        coords: { lat: Number(docCoords.lat), lng: Number(docCoords.lng) },
        address: practice?.address || selectedDoctor.practiceAddress || selectedDoctor.address || null,
      },
      practiceDetails: practice || selectedDoctor._practice || {},
      bookLater: true,
      bookingDate,
    };

    setEstimateRequested(true);
    dispatch(getEstimate(bookingData));
    setShowEstimateDialog(true);
  };

  // Accept estimate and create booking: notify parent then create booking
  const handleAcceptEstimateAndCreate = () => {
    setShowEstimateDialog(false);
    setEstimateRequested(false);

    // notify parent about chosen ISO dates (if available)
    if (chosenSlot && typeof onAssignDate === "function") {
      try {
        onAssignDate(chosenSlot.start.toISOString(), chosenSlot.end.toISOString());
      } catch (e) { }
    }

    handleConfirmSlot();
  };

  const handleCloseEstimateDialog = () => {
    setShowEstimateDialog(false);
    setEstimateRequested(false);
  };

  const initials = (d) => {
    const fn = d.firstName || "";
    const ln = d.lastName || "";
    return (fn[0] || "") + (ln[0] || "");
  };

  // ----------------------
  // RENDER
  // ----------------------
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ style: { minHeight: "75vh", padding: 8 } }}>
      <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">Select Doctor & Time</Typography>
        <Typography variant="body2" color="textSecondary">30-minute consultations</Typography>
      </DialogTitle>

      <DialogContent style={{ padding: 16 }}>
        {!selectedDoctor ? (
          <Paper elevation={3} style={{ padding: 16 }}>
            {/* Location toggle + radius + request location */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                <FormControlLabel
                  control={<Switch checked={useLocation} onChange={(e) => setUseLocation(Boolean(e.target.checked))} color="primary" />}
                  label={useLocation ? "Use my location" : "Show all doctors"}
                />
                {useLocation && (
                  <>
                    <TextField
                      size="small"
                      label="Radius"
                      value={maxDistanceKm}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isNaN(v) || v < 0) return;
                        setMaxDistanceKm(v);
                      }}
                      InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                      style={{ width: 110 }}
                    />
                    <Button size="small" onClick={requestUserLocation} disabled={requestingLocation}>
                      {requestingLocation ? "Requesting..." : userLocation ? "Refresh" : "Allow location"}
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            {/* Search + speciality filter */}
            <Box mb={2}>
              <TextField fullWidth label="Search name / email / mobile" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} variant="outlined" />
            </Box>

            <Box mb={2}>
              <Autocomplete
                options={(doctortypes && doctortypes.length ? doctortypes : specialitytypes) || []}
                getOptionLabel={(opt) => (opt ? String(opt) : "")}
                value={filterValue}
                onChange={(e, val) => setFilterValue(val || "")}
                renderInput={(params) => <TextField {...params} label="Filter by speciality" variant="outlined" />}
                freeSolo
              />
            </Box>

            <Divider />

            {/* Doctor list */}
            <div style={{ maxHeight: "56vh", overflowY: "auto", marginTop: 12 }}>
              {filteredDoctors && filteredDoctors.length ? (
                filteredDoctors.map((doc) => {
                  const key = doc.id || doc.uid || doc._id || `${doc.firstName || ""}-${doc.lastName || ""}`;
                  const distanceLabel = doc._distanceKm != null ? `${doc._distanceKm.toFixed(1)} km` : (useLocation ? "—" : "—");
                  const disableSelect = useLocation && (!doc._coords || !doc._hasPracticeAddress);
                  return (
                    <Paper key={key} style={{ padding: 12, marginBottom: 12 }} elevation={1}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item>
                          <Avatar>{initials(doc)}</Avatar>
                        </Grid>
                        <Grid item xs>
                          <div style={{ fontWeight: 700 }}>{doc.firstName} {doc.lastName}</div>
                          <div style={{ fontSize: 13, color: "#666" }}>{doc.specialityType || doc.practice || doc.doctorSpeciality}</div>
                          <div style={{ fontSize: 12, color: "#666" }}>{doc.email} • {doc.mobile}</div>
                          <div style={{ fontSize: 12, color: "#666" }}>
                            <strong>Practice:</strong> {doc._practice?.practiceName || (doc._practices && doc._practices.length ? doc._practices[0].practiceName : "Unknown")}
                            {doc._practice?.address ? ` — ${doc._practice.address}` : (doc._practices && doc._practices.length && doc._practices[0].address ? ` — ${doc._practices[0].address}` : "")}
                          </div>
                          <div style={{ fontSize: 12, color: "#666" }}><strong>Distance:</strong> {distanceLabel}</div>
                        </Grid>
                        <Grid item>
                          <Button variant="contained" color="primary" disabled={disableSelect} onClick={() => handleSelectDoctor(doc)}>
                            Select
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {useLocation ? (userLocation ? "No nearby doctors found in your radius." : "Waiting for location permission...") : "No doctors found."}
                </Typography>
              )}
            </div>
          </Paper>
        ) : (
          /* SLOT VIEW (single _practice) */
          <Paper elevation={3} style={{ padding: 16 }}>
            <Box display="flex" alignItems="center" mb={1}>
              <IconButton onClick={handleBackToList} size="small"><ArrowBackIcon /></IconButton>
              <Box ml={1}>
                <Typography variant="h6" style={{ marginTop: 6 }}>{selectedDoctor.firstName} {selectedDoctor.lastName}</Typography>
                <Typography variant="body2" color="textSecondary">{selectedDoctor.specialityType || selectedDoctor.practice}</Typography>
              </Box>
            </Box>

            <Divider style={{ margin: "8px 0" }} />

            <Grid container spacing={2}>
              <Grid item>
                <Avatar style={{ width: 64, height: 64 }}>{initials(selectedDoctor)}</Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="subtitle2">{selectedDoctor.email}</Typography>
                <Typography variant="subtitle2">{selectedDoctor.mobile}</Typography>

                <Box mt={1} mb={1}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {selectedDoctor._practice?.practiceName || "Practice"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedDoctor._practice?.address || selectedDoctor._practice?.addressObj?.formattedAddress || "Address unknown"}
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary">
                  Address: {selectedDoctor._practice?.address || selectedDoctor._practice?.addressObj?.formattedAddress || "Unknown"}
                </Typography>
              </Grid>
            </Grid>

            <Divider style={{ margin: "12px 0" }} />

            <Typography variant="body2" color="textSecondary">
              Select a 30-minute slot for {initialSlotDate ? new Date(initialSlotDate).toLocaleDateString() : new Date().toLocaleDateString()}:
            </Typography>

            <div style={{ maxHeight: "52vh", overflowY: "auto", marginTop: 8 }}>
              {availableSlots && availableSlots.length ? (
                availableSlots.map((s, i) => {
                  const label = `${pad(s.start.getHours())}:${pad(s.start.getMinutes())} - ${pad(s.end.getHours())}:${pad(s.end.getMinutes())}`;
                  const isChosen = chosenSlot && chosenSlot.start.getTime() === s.start.getTime();
                  return (
                    <Box key={i} mb={1} display="flex" alignItems="center">
                      <Button
                        variant={isChosen ? "contained" : "outlined"}
                        color={isChosen ? "primary" : "default"}
                        disabled={s.disabled}
                        onClick={() => setChosenSlot({ start: s.start, end: s.end })}
                        style={{ flex: 1, justifyContent: "flex-start" }}
                      >
                        <div style={{ textAlign: "left" }}>
                          <div style={{ fontWeight: 600 }}>{label}</div>
                          {s.conflictWith && <div style={{ fontSize: 12, color: "#d32f2f" }}>Conflicts with existing booking</div>}
                          {s.disabled && !s.conflictWith && <div style={{ fontSize: 12, color: "#666" }}>Unavailable</div>}
                        </div>
                      </Button>
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary">No slots available for that date.</Typography>
              )}
            </div>

            <Box mt={2} display="flex" justifyContent="flex-end" style={{ gap: 12 }}>
              <Button onClick={handleBackToList}>Back</Button>
              <Button variant="contained" color="primary" disabled={!chosenSlot} onClick={handleConfirmSlot}>Confirm time</Button>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px" }}>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>

      {/* Estimate dialog (kept for completeness) */}
      <Dialog open={showEstimateDialog} onClose={handleCloseEstimateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Estimate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This estimate was requested for the selected doctor & slot. Review and accept to create the booking.
          </DialogContentText>

          {estimateRequested && !estimatedata?.estimate && (
            <Typography variant="body2" color="textSecondary">Waiting for estimate...</Typography>
          )}

          {estimatedata && estimatedata.estimate && (
            <Box mt={2}>
              <Typography><strong>Estimate Amount:</strong> {estimatedata.estimate.estimateFare}</Typography>
              <Typography><strong>Treatment Fee:</strong> {estimatedata.estimate.treatment_fee}</Typography>
              <Typography><strong>Total:</strong> {estimatedata.estimate.bigGrand}</Typography>
            </Box>
          )}

          {estimatedata && estimatedata.error && (
            <Typography color="error">{estimatedata.error.msg || "Error getting estimate"}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEstimateDialog}>Cancel</Button>
          <Button onClick={handleAcceptEstimateAndCreate} color="primary" variant="contained">Accept & Create Booking</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
